/**
 * ExportMenu — FRD §32
 * Reusable 3-format export button used across all admin list modules.
 * Supports CSV (opens in Excel), PDF (print dialog), and JSON download.
 * Usage: <ExportMenu rows={rows} columns={columns} filename="students" />
 */
import React, { useState } from "react";
import {
  Button, Menu, MenuItem, ListItemIcon, ListItemText, Divider
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import TableChartOutlinedIcon   from "@mui/icons-material/TableChartOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import CodeOutlinedIcon         from "@mui/icons-material/CodeOutlined";
import KeyboardArrowDownIcon    from "@mui/icons-material/KeyboardArrowDown";

/* ── Helpers ── */
const toCSV = (columns, rows) => {
  const header = columns.map(c => `"${c.label}"`).join(",");
  const body   = rows.map(r =>
    columns.map(c => {
      const val = r[c.field];
      if (val === null || val === undefined || typeof val === "object") return '""';
      return `"${String(val).replace(/"/g, '""')}"`;
    }).join(",")
  ).join("\n");
  return `${header}\n${body}`;
};

const downloadBlob = (content, filename, mime) => {
  const blob = new Blob([content], { type: mime });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const buildFilename = (base, ext) => {
  const date = new Date().toISOString().slice(0, 10);
  return `${base}_${date}.${ext}`;
};

/* ── Component ── */
export default function ExportMenu({
  rows      = [],
  columns   = [],
  filename  = "export",
  buttonSx  = {},
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleCSV = () => {
    const csv = toCSV(columns, rows);
    downloadBlob(csv, buildFilename(filename, "csv"), "text/csv;charset=utf-8;");
    setAnchorEl(null);
  };

  const handlePDF = () => {
    setAnchorEl(null);
    // Use browser's print-to-PDF
    window.print();
  };

  const handleJSON = () => {
    const clean = rows.map(r =>
      Object.fromEntries(
        columns.map(c => [c.label, typeof r[c.field] === "object" ? String(r[c.field]) : r[c.field]])
      )
    );
    downloadBlob(JSON.stringify(clean, null, 2), buildFilename(filename, "json"), "application/json");
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<FileDownloadOutlinedIcon />}
        endIcon={<KeyboardArrowDownIcon />}
        onClick={e => setAnchorEl(e.currentTarget)}
        sx={{
          textTransform: "none",
          borderRadius: "8px",
          fontSize: "13px",
          border: "1px solid #D5D7DA",
          color: "#374151",
          "&:hover": { borderColor: "#4A7BA8", color: "#244C77" },
          ...buttonSx,
        }}
      >
        Export
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: { borderRadius: "10px", minWidth: 180, boxShadow: "0 4px 16px rgba(0,0,0,0.12)" } }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleCSV} sx={{ fontSize: "13px", py: 1 }}>
          <ListItemIcon><TableChartOutlinedIcon sx={{ fontSize: "18px", color: "#2e7d32" }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: "13px" }}>CSV / Excel (.csv)</ListItemText>
        </MenuItem>
        <MenuItem onClick={handlePDF} sx={{ fontSize: "13px", py: 1 }}>
          <ListItemIcon><PictureAsPdfOutlinedIcon sx={{ fontSize: "18px", color: "#c62828" }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: "13px" }}>PDF (Print)</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleJSON} sx={{ fontSize: "13px", py: 1 }}>
          <ListItemIcon><CodeOutlinedIcon sx={{ fontSize: "18px", color: "#244C77" }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: "13px" }}>JSON (.json)</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
